import { Header } from '@/components/header'
import { Footer } from '@/components/footer'
import { AssociationsAndSuppliers } from '@/components/AssociationsAndSuppliers'
import { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'

// Types for images
interface ImageItem {
  src: string
  alt: string
}

export default function RailingsGallery() {
  const [displayedImages, setDisplayedImages] = useState<ImageItem[]>([])
  
  // State for lightbox
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxSlide, setLightboxSlide] = useState(0)
  const [lightboxAnimation, setLightboxAnimation] = useState('entering') // 'entering', 'visible', 'exiting'
  
  // Handle client-side navigation
  const handleNavigation = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault()
    window.history.pushState({}, '', href)
    window.dispatchEvent(new Event('popstate'))
    window.scrollTo(0, 0)
  }

  // Function to parse image filename into alt text
  function parseImageFileName(filename: string): string {
    // Remove file extension
    const withoutExtension = filename.substring(0, filename.lastIndexOf('.'))
    // Replace hyphens with spaces
    return withoutExtension.replace(/-/g, ' ')
  }

  // Function to fix path for production and development consistency
  function fixImagePath(path: string): string {
    // Remove /public prefix if it exists (for dev mode)
    let imagePath = path.replace('/public', '')
    
    // Ensure path starts with a slash
    if (!imagePath.startsWith('/')) {
      imagePath = '/' + imagePath
    }
    
    return imagePath
  }

  // Load images dynamically
  useEffect(() => {
    try {
      // Using Vite's import.meta.glob to get all relevant images from various folders
      const railingImagesModules = import.meta.glob('/public/assets/images/Railing/**/*.{jpg,jpeg,png,webp}', { eager: true })
      
      // Also get deck images that have railing-related terms in them
      const deckRailingImagesModules = import.meta.glob('/public/assets/images/Decks/**/*railing*.{jpg,jpeg,png,webp}', { eager: true })
      
      // Additional images from other locations
      const additionalImagesModules = import.meta.glob('/public/assets/images/2020/04/*2240*.{jpg,jpeg,png}', { eager: true })
      const moreImagesModules = import.meta.glob('/public/assets/images/2020/04/*2335*.{jpg,jpeg,png}', { eager: true })
      
      const loadedImages: ImageItem[] = []

      // Process railing folder images
      Object.keys(railingImagesModules).forEach(path => {
        const imagePath = fixImagePath(path)
        
        // Get filename for alt text
        const fileName = path.substring(path.lastIndexOf('/') + 1)
        const altText = parseImageFileName(fileName)
        
        loadedImages.push({
          src: imagePath,
          alt: altText
        })
      })
      
      // Process deck images that have railings
      Object.keys(deckRailingImagesModules).forEach(path => {
        const imagePath = fixImagePath(path)
        
        // Get filename for alt text
        const fileName = path.substring(path.lastIndexOf('/') + 1)
        const altText = parseImageFileName(fileName)
        
        loadedImages.push({
          src: imagePath,
          alt: altText
        })
      })
      
      // Process additional images
      Object.keys(additionalImagesModules).forEach(path => {
        const imagePath = fixImagePath(path)
        loadedImages.push({
          src: imagePath,
          alt: "Aluminum picket railing"
        })
      })
      
      Object.keys(moreImagesModules).forEach(path => {
        const imagePath = fixImagePath(path)
        loadedImages.push({
          src: imagePath,
          alt: "Modern aluminum railing"
        })
      })

      setDisplayedImages(loadedImages)
    } catch (error) {
      console.error('Error loading images:', error)
    }
  }, [])
  
  // Lightbox functions
  const openLightbox = (index: number) => {
    setLightboxSlide(index)
    setLightboxOpen(true)
    setLightboxAnimation('entering')
    // Start animation
    setTimeout(() => setLightboxAnimation('visible'), 50)
    // Prevent body scrolling when lightbox is open
    document.body.style.overflow = 'hidden'
  }
  
  const closeLightbox = () => {
    // Start exit animation
    setLightboxAnimation('exiting')
    // Wait for animation to finish before fully closing
    setTimeout(() => {
      setLightboxOpen(false)
      // Restore body scrolling
      document.body.style.overflow = 'visible'
    }, 300) // Should match the animation duration
  }
  
  const goToPrevLightboxSlide = () => {
    setLightboxSlide((prev) => 
      prev === 0 ? displayedImages.length - 1 : prev - 1
    )
  }
  
  const goToNextLightboxSlide = () => {
    setLightboxSlide((prev) => 
      (prev + 1) % displayedImages.length
    )
  }
  
  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!lightboxOpen) return
      
      switch (e.key) {
        case 'ArrowLeft':
          goToPrevLightboxSlide()
          break
        case 'ArrowRight':
          goToNextLightboxSlide()
          break
        case 'Escape':
          closeLightbox()
          break
        default:
          break
      }
    }
    
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [lightboxOpen, displayedImages.length])

  return (
    <main>
      <Helmet>
        <title>Railing Gallery | Busy Beaver Construction | Calgary</title>
        <meta name="description" content="View our gallery of custom railings for Calgary decks featuring aluminum, glass, cable, and wood options. Get inspiration for safe, stylish deck railings." />
        <meta name="keywords" content="deck railings Calgary, aluminum railings, glass railings, cable railings, wood railings, custom deck railings, modern railings, safety railings" />
        <link rel="canonical" href="https://busybeaverconstruction.com/gallery/railings" />
        <meta property="og:title" content="Railing Gallery | Busy Beaver Construction" />
        <meta property="og:description" content="Browse our portfolio of custom deck railings designed to enhance the safety and appearance of Calgary outdoor spaces with quality materials and craftsmanship." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://busybeaverconstruction.com/gallery/railings" />
        <meta property="og:image" content="https://busybeaverconstruction.com/assets/images/Railing/Aluminum-picket-railing-in-Black.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="preload" href="/assets/images/Railing/Aluminum-picket-railing-in-Black.jpg" as="image" />
      </Helmet>
      <Header />
      
      {/* Hero Banner */}
      <section className="relative h-[400px] flex items-center justify-center">
        <img 
          src="/assets/images/Railing/Aluminum-picket-railing-in-Black.jpg"
          alt="Black aluminum picket railings on custom deck by Busy Beaver Construction in Calgary"
          className="absolute inset-0 w-full h-full object-cover loading-eager"
          fetchPriority="high"
        />        
        <div className="relative z-10 mt-64 text-center">
          <div className="inline-block bg-busy-green rounded py-3 mx-4 px-4 lg:px-8">
            <p className="text-white text-base font-medium rounded">Calgary's Leading Custom Deck & Fence</p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-12 md:py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-col items-center mb-8 md:mb-14">
            <h1 className="text-center text-5xl lg:text-6xl font-primary">Railings Services</h1>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
            <div className="space-y-6 text-base text-gray-500 leading-relaxed">
              <p>
                Deck railing is like the icing on a cake, it's one of the final details to be added to your deck building project. 
                A guard system is generally required around the perimeter of any deck 24 inches above grade. Your railing can match 
                the material on your deck surface, or you can mix it up with a colorful aluminum railing.
              </p>
              
              <p>
                There are various deck railing materials to choose from, such as the following:
              </p>
              
              <p>
                <strong>Wood</strong> – A traditional choice, wood railings can be hand crafted easily, and generally built using 
                Pressure Treated wood or Cedar. Wood railings will require the most maintenance over other railing products, as it 
                will need painted or stained for longevity, but is the most budget friendly option.
              </p>
              
              <p>
                <strong>Composite</strong> – A great no maintenance option to be paired with a no maintenance deck. This type of 
                railing can withstand our changing temperatures, adds richness, and complements a composite/pvc or vinyl deck.
              </p>
              
              <p>
                <strong>Aluminum</strong> – The most commonly used railings would be aluminum. This type of railing requires very 
                little to no maintenance. Standard picket railing is widely used, as it's the most economical, but there are many 
                options for upgraded custom railings in Aluminum.
              </p>
              
              <p>
                <strong>Glass</strong> – Glass is very elegant and stylish, and is a great option when you want to see the views 
                your deck has to offer. The exterior frame and posts are generally made from aluminum, and glass options include; 
                clear, privacy, or smoked. Topless glass railings are on the higher end of the scale budget wise, but are widely 
                used when homeowners want a plane view of sight. Glass railings come in various heights to create a wind wall or 
                additional privacy.
              </p>
            </div>
            
            <div className="space-y-6">
              <div className="overflow-hidden h-[460px] md:h-[400px]">
                <img 
                  src="/assets/images/2020/04/IMG_2240.jpg"
                  alt="Custom aluminum picket railing on elevated deck by Busy Beaver Construction Calgary"
                  className="w-full h-full object-cover loading-lazy"
                />
              </div>
              <div className="overflow-hidden h-[320px] md:h-[400px]">
                <img 
                  src="/assets/images/2020/04/IMG_2335.jpg"
                  alt="Premium black aluminum railings for multi-level deck in Calgary by Busy Beaver Construction"
                  className="w-full h-full object-cover loading-lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Content */}
      <section className="py-12 md:py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-col items-center mb-8 md:mb-14">
            <h1 className="text-center text-5xl lg:text-6xl font-primary">Railings Gallery</h1>
          </div>
          
          {/* Gallery Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {displayedImages.map((image, index) => (
              <div 
                key={index} 
                className="overflow-hidden rounded shadow-lg hover:shadow-xl transition-shadow duration-300 bg-white cursor-pointer"
                onClick={() => openLightbox(index)}
              >
                <div className="h-[280px] md:h-[320px]">
                  <img 
                    src={image.src}
                    alt={image.alt}
                    className="w-full h-full object-cover loading-lazy"
                  />
                </div>
                <div className="p-4">
                  <p className="text-sm text-gray-500">{image.alt}</p>
                </div>
              </div>
            ))}
          </div>
          
          {/* No results message */}
          {displayedImages.length === 0 && (
            <div className="text-center py-8">
              <p className="text-gray-700">Loading images...</p>
            </div>
          )}
          
          <div className="text-center mt-12">
            <p className="mb-6 text-gray-700">
              Looking for the perfect railing for your deck or outdoor space? Contact us to discuss options and get a free estimate.
            </p>
            <a
              href="/estimate" 
              className="inline-block px-6 py-3 bg-beaver-green text-black font-medium rounded hover:bg-beaver-green-dark hover:brightness-110 transition-all duration-300"
              onClick={(e) => handleNavigation(e, '/estimate')}
            >
              GET FREE ESTIMATE
            </a>
          </div>
        </div>
      </section>
      
      {/* WE DO DAM GOOD WORK Section */}
      <section className="py-10 bg-gray-900 text-white text-center">
        <h2 className="text-4xl md:text-5xl font-bold tracking-wide">
          WE DO <span className="text-red-600">DAM</span> GOOD WORK
        </h2>
      </section>
      
      {/* Associations and Footer */}
      <AssociationsAndSuppliers />
      <Footer />
      
      {/* Lightbox */}
      {lightboxOpen && displayedImages.length > 0 && (
        <div 
          className={`fixed inset-0 bg-black z-50 flex items-center justify-center transition-opacity duration-300 ${
            lightboxAnimation === 'entering' ? 'opacity-0' : 
            lightboxAnimation === 'exiting' ? 'opacity-0' : 'opacity-100'
          }`}
        >
          <div 
            className="absolute inset-0"
            onClick={closeLightbox}
          />
          
          <div className="relative max-w-7xl w-full h-screen max-h-screen flex items-center justify-center p-4">
            {/* Close button */}
            <button 
              onClick={closeLightbox}
              className={`absolute top-4 right-4 z-10 bg-black/50 hover:bg-beaver-green text-white p-3 rounded-full transition-all duration-300 focus:outline-none ${
                lightboxAnimation === 'entering' ? 'opacity-0 translate-y-[-20px]' : 
                lightboxAnimation === 'exiting' ? 'opacity-0 translate-y-[-20px]' : 'opacity-100 translate-y-0'
              }`}
              aria-label="Close lightbox"
            >
              <i className="fas fa-times text-2xl"></i>
            </button>
            
            {/* Image container */}
            <div className="relative w-full h-full flex items-center justify-center">
              <img
                src={displayedImages[lightboxSlide].src}
                alt={displayedImages[lightboxSlide].alt}
                className={`max-w-full max-h-full object-contain transition-all duration-300 ${
                  lightboxAnimation === 'entering' ? 'opacity-0 scale-90' : 
                  lightboxAnimation === 'exiting' ? 'opacity-0 scale-90' : 'opacity-100 scale-100'
                }`}
              />
              
              {/* Image caption */}
              <div 
                className={`absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/70 text-white py-2 px-4 rounded text-center max-w-lg transition-all duration-300 ${
                  lightboxAnimation === 'entering' ? 'opacity-0 translate-y-[20px]' : 
                  lightboxAnimation === 'exiting' ? 'opacity-0 translate-y-[20px]' : 'opacity-100 translate-y-0'
                }`}
              >
                {displayedImages[lightboxSlide].alt}
              </div>
              
              {/* Navigation buttons */}
              <button 
                onClick={(e) => {
                  e.stopPropagation()
                  goToPrevLightboxSlide()
                }}
                className={`absolute left-[0px] md:left-[60px] lg:left-[-180px] top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-beaver-green text-white p-3 rounded-full transition-all duration-300 focus:outline-none ${
                  lightboxAnimation === 'entering' ? 'opacity-0 translate-x-[-20px]' : 
                  lightboxAnimation === 'exiting' ? 'opacity-0 translate-x-[-20px]' : 'opacity-100 translate-x-0'
                }`}
                aria-label="Previous slide"
              >
                <i className="fas fa-chevron-left text-2xl"></i>
              </button>
              
              <button 
                onClick={(e) => {
                  e.stopPropagation()
                  goToNextLightboxSlide()
                }}
                className={`absolute right-[0px] md:right-[60px] lg:right-[-180px] top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-beaver-green text-white p-3 rounded-full transition-all duration-300 focus:outline-none ${
                  lightboxAnimation === 'entering' ? 'opacity-0 translate-x-[20px]' : 
                  lightboxAnimation === 'exiting' ? 'opacity-0 translate-x-[20px]' : 'opacity-100 translate-x-0'
                }`}
                aria-label="Next slide"
              >
                <i className="fas fa-chevron-right text-2xl"></i>
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  )
} 